import type { Contract } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Building2, CircleDot, MoreHorizontal, PlusCircle, Ship } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useContracts, useCustomer, useDeleteContract, useObjects } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { SearchCombobox, SearchField } from '@/shared/components/inputs';
import { PageHeading } from '@/shared/components/layout';
import { DataTablePagination, SortableHeader, TableLoadingCell } from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import { TimelineControls } from '@/shared/components/ui/timeline-controls';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { getSortParam } from '@/shared/utils/table-utils';

import { ContractsTimeline, type ContractsTimelineHandle } from '../components/contracts-timeline';
import { CopyContractModal } from '../components/copy-contract-modal';
import { ViewSelect } from '../components/view-select';

const CustomerCell = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);
  if (isLoading) return <Skeleton className="h-4 w-24" />;
  return (
    <div className="truncate max-w-[180px]" title={customer?.name || '-'}>
      {customer?.name || '-'}
    </div>
  );
};

const ObjectCell = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (!objectIds || objectIds.length === 0) {
    return <div>-</div>;
  }

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />;
  }

  const objects = objectsData?.docs || [];

  if (objects.length === 0) {
    return <div>-</div>;
  }

  if (objects.length === 1) {
    return (
      <div className="truncate max-w-[180px]" title={objects[0].name}>
        {objects[0].name}
      </div>
    );
  }

  // Show first 2 object names and count if more
  const displayObjects = objects.slice(0, 2);
  const remainingCount = objects.length - displayObjects.length;
  const allObjectNames = objects.map((obj) => obj.name);

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="truncate max-w-[180px]">
          {displayObjects.map((obj) => obj.name).join(', ')}
          {remainingCount > 0 && ` +${remainingCount} more`}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-xs">
        <div className="text-sm">
          {allObjectNames.map((name, index) => (
            <div key={index}>{name}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const ActionCell = ({
  contract,
  onDelete,
  onCopy,
  basePath,
  isGmnAdmin,
}: {
  contract: Contract;
  onDelete: (contract: Contract) => void;
  onCopy: (contract: Contract) => void;
  basePath: string;
  isGmnAdmin: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="size-8 p-0">
          <span className="sr-only">{t('navigation.openMenu')}</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate(`${basePath}/${contract.id}`)}>
          {t('common.edit')}
        </DropdownMenuItem>
        {isGmnAdmin && (
          <DropdownMenuItem onClick={() => onCopy(contract)}>
            {t('contracts.copyContract')}
          </DropdownMenuItem>
        )}
        {isGmnAdmin && (
          <DropdownMenuItem onClick={() => onDelete(contract)}>
            {t('common.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function ContractsOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGmnAdmin = user?.role === 'gmn-admin';
  const basePath = '/gmn/contract-management/contracts';

  const tablePreferences = useTablePreferences({
    tableId: 'contracts-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      view: 'table',
      searchTerm: '',
      selectedFilters: {
        customerId: undefined,
        objectId: undefined,
        status: undefined,
      },
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(
    (preferences.selectedFilters?.customerId as string) || undefined
  );
  const [selectedObjectId, setSelectedObjectId] = useState<string | undefined>(
    (preferences.selectedFilters?.objectId as string) || undefined
  );
  const [selectedStatus, setSelectedStatus] = useState<'open' | 'closed' | undefined>(
    (preferences.selectedFilters?.status as 'open' | 'closed') || undefined
  );
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );
  const [view, setView] = useState<'table' | 'timeline'>(
    (preferences.view as 'table' | 'timeline') || 'table'
  );

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      view,
      searchTerm,
      selectedFilters: {
        customerId: selectedCustomerId,
        objectId: selectedObjectId,
        status: selectedStatus,
      },
    });
  }, [
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    view,
    searchTerm,
    selectedCustomerId,
    selectedObjectId,
    selectedStatus,
  ]);

  const columns: ColumnDef<Contract>[] = [
    {
      accessorKey: 'title',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.title')} />,
      cell: ({ row }) => (
        <div className="truncate max-w-[180px]" title={row.getValue('title')}>
          {row.getValue('title')}
        </div>
      ),
      size: 180,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'contractNumber',
      header: ({ column }) => (
        <SortableHeader column={column} title={t('contracts.contractNumber')} />
      ),
      cell: ({ row }) => (
        <div className="truncate max-w-[150px]" title={row.getValue('contractNumber')}>
          {row.getValue('contractNumber')}
        </div>
      ),
      size: 150,
      minSize: 120,
      maxSize: 200,
    },

    {
      accessorKey: 'customer',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.customer')} />,
      cell: ({ row }) => <CustomerCell customerId={row.getValue('customer') as string} />,
      size: 200,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'aggregatedObjects',
      header: () => <div className="text-left font-medium">{t('objects.objects')}</div>,
      cell: ({ row }) => {
        const aggregatedObjects = row.getValue('aggregatedObjects') as (string | { id: string })[];
        const objectIds =
          aggregatedObjects?.map((obj) => (typeof obj === 'string' ? obj : obj.id)) || [];
        return <ObjectCell objectIds={objectIds} />;
      },
      size: 200,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'startDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.startDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('startDate') as string;
        return <div>{date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}</div>;
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'endDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.endDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('endDate') as string;
        return <div>{date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}</div>;
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.statusLabel')} />,
      cell: ({ row }) => {
        const status = row.getValue('status') as 'open' | 'closed';
        return (
          <Badge variant={status === 'closed' ? 'secondary' : 'default'}>
            {status === 'closed' ? t('contracts.status.closed') : t('contracts.status.open')}
          </Badge>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return <div>{DateTime.fromISO(date).toFormat('dd.MM.yyyy')}</div>;
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },

    {
      id: 'actions',
      cell: ({ row }) => (
        <ActionCell
          contract={row.original}
          onDelete={setContractToDelete}
          onCopy={setContractToCopy}
          basePath={basePath}
          isGmnAdmin={!!isGmnAdmin}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: contractsData, isLoading } = useContracts({
    page: view === 'table' ? pagination.pageIndex + 1 : 1,
    limit: view === 'table' ? pagination.pageSize : 0,
    status: selectedStatus,
    where: {
      and: [
        ...(searchTerm
          ? [
              {
                or: [
                  {
                    contractNumber: {
                      contains: searchTerm,
                    },
                  },
                  {
                    title: {
                      contains: searchTerm,
                    },
                  },
                ],
              },
            ]
          : []),
        ...(selectedCustomerId
          ? [
              {
                customer: {
                  equals: selectedCustomerId,
                },
              },
            ]
          : []),
        ...(selectedObjectId
          ? [
              {
                aggregatedObjects: {
                  in: [selectedObjectId],
                },
              },
            ]
          : []),
      ],
    },
    sort: getSortParam<Contract>(sorting, {
      customer: 'customer.name',
    }),
  });

  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [contractToCopy, setContractToCopy] = useState<Contract | null>(null);
  const deleteContract = useDeleteContract();

  const handleDeleteContract = async () => {
    if (contractToDelete) {
      try {
        await deleteContract.mutateAsync(contractToDelete.id);
        toast.success(t('contracts.contractDeletedSuccessfully'));
        setContractToDelete(null);
      } catch (_error) {
        toast.error(t('contracts.errorDeletingContract'));
      }
    }
  };

  const table = useReactTable({
    data: contractsData?.docs ?? [],
    columns,
    pageCount: contractsData?.totalPages ?? 0,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const timelineRef = useRef<ContractsTimelineHandle>(null);

  return (
    <TooltipProvider>
      <PageHeading>{t('contracts.contracts')}</PageHeading>

      <div className="flex items-center">
        <SearchField searchTerm={searchTerm} setSearchTerm={handleSearchChange} />
        <div className="ml-auto flex items-center gap-4">
          {view === 'timeline' && (
            <TimelineControls
              onExpandAll={() => timelineRef.current?.handleExpandAll()}
              onCollapseAll={() => timelineRef.current?.handleCollapseAll()}
              onZoomIn={() => timelineRef.current?.handleZoomIn()}
              onZoomOut={() => timelineRef.current?.handleZoomOut()}
            />
          )}
          <ViewSelect view={view} onViewChange={(value) => setView(value)} />

          {/* Status Filter */}
          <Select
            value={selectedStatus || 'all'}
            onValueChange={(value) => {
              setSelectedStatus(value === 'all' ? undefined : (value as 'open' | 'closed'));
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="h-9 min-w-[140px] w-auto">
              <div className="flex items-center">
                <CircleDot className="size-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder={t('contracts.statusFilter')} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="open">{t('contracts.status.open')}</SelectItem>
              <SelectItem value="closed">{t('contracts.status.closed')}</SelectItem>
            </SelectContent>
          </Select>

          <SearchCombobox
            value={selectedCustomerId}
            onSelect={(value) => {
              setSelectedCustomerId(value || undefined);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            collectionKey="customers"
            searchKey="name"
            className="h-9 min-w-[200px] w-auto"
            texts={{ selectItemMsg: t('customers.customer') }}
            icon={Building2}
          />
          <SearchCombobox
            value={selectedObjectId}
            onSelect={(value) => {
              setSelectedObjectId(value || undefined);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            collectionKey="objects"
            searchKey="name"
            className="h-9 min-w-[200px] w-auto"
            texts={{ selectItemMsg: `${t('objects.object')}` }}
            icon={Ship}
          />
          <Button onClick={() => navigate(`${basePath}/new`)} disabled={!isGmnAdmin}>
            <PlusCircle className="size-4 mr-2" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
              {t('contracts.addContract')}
            </span>
          </Button>
        </div>
      </div>

      <div className="space-y-4 overflow-x-hidden max-w-[calc(100vw-304px)]">
        {view === 'table' ? (
          <>
            <div className="w-full overflow-auto">
              <Table className="w-full table-fixed">
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="group">
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} style={{ width: `${header.getSize()}px` }}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableLoadingCell colSpan={columns.length} />
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <EmptyTableRow colSpan={columns.length} />
                  )}
                </TableBody>
              </Table>
            </div>

            <DataTablePagination table={table} />
          </>
        ) : contractsData?.docs && contractsData?.docs.length > 0 ? (
          <ContractsTimeline
            ref={timelineRef}
            contracts={contractsData?.docs || []}
            groupByCustomer
            onDelete={isGmnAdmin ? setContractToDelete : undefined}
            onCopy={isGmnAdmin ? setContractToCopy : undefined}
          />
        ) : (
          <div className="text-center text-muted-foreground text-sm">{t('common.noResults')}</div>
        )}
      </div>

      <AlertDialog
        open={!!contractToDelete}
        onOpenChange={(open) => !open && setContractToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contracts.deleteContract')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contracts.deleteContractConfirmation', {
                name: contractToDelete?.contractNumber || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContract}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CopyContractModal
        contract={contractToCopy}
        open={!!contractToCopy}
        onOpenChange={(open) => !open && setContractToCopy(null)}
      />
    </TooltipProvider>
  );
}
